import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaChartPie,
    FaBriefcase,
    FaUsers,
    FaCalendarAlt,
    FaComments,
    FaChartBar,
    FaBuilding,
    FaBell,
    FaSignOutAlt,
    FaChevronLeft,
    FaUser,
    FaHome
} from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import { API_URL } from '../../config';
import useAuthGuard from '../../hooks/useAuthGuard';
import './RecruiterLayout.css';

const RecruiterLayout = () => {
    const { user, logout } = React.useContext(AuthContext);
    useAuthGuard(['recruiter']);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [recruiterName, setRecruiterName] = useState('Recruiter');
    const [profileImage, setProfileImage] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const navigate = useNavigate();

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const res = await axios.get('/api/users/me', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data) {
                    setRecruiterName(res.data.name || 'Recruiter');
                    setProfileImage(res.data.profileImage || '');
                }
            } catch (err) {
                console.error("Error fetching recruiter profile:", err);
            }
        };
        fetchProfile();

        const handleProfileUpdate = () => {
            fetchProfile();
        };
        window.addEventListener('profileUpdated', handleProfileUpdate);
        return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
    }, []);

    useEffect(() => {
        const fetchUnreadCount = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const res = await axios.get('/api/notifications', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const unread = res.data.filter(n => !n.isRead).length;
                setUnreadCount(unread);
            } catch (err) {
                // silent fail
            }
        };
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 60000);

        const handleNotificationsRead = (e) => {
            setUnreadCount(e.detail?.count ?? 0);
        };
        window.addEventListener('notificationsRead', handleNotificationsRead);

        return () => {
            clearInterval(interval);
            window.removeEventListener('notificationsRead', handleNotificationsRead);
        };
    }, []);

    const handleLogout = () => {
        logout();
        window.dispatchEvent(new Event('authChange'));
        navigate('/auth');
    };

    const menuGroups = [
        {
            heading: 'Navigation',
            items: [
                { path: '/', name: 'Public Home', icon: <FaHome /> }
            ]
        },
        {
            heading: 'Main',
            items: [
                { path: '/recruiter', name: 'Hiring Hub', icon: <FaChartPie /> }
            ]
        },
        {
            heading: 'Recruitment',
            items: [
                { path: '/recruiter/company', name: 'Company Branding', icon: <FaBuilding /> },
                { path: '/recruiter/jobs', name: 'Job Lifecycle', icon: <FaBriefcase /> },
                { path: '/recruiter/applicants', name: 'Talent Sourcing', icon: <FaUsers /> },
                { path: '/recruiter/interviews', name: 'Interview Center', icon: <FaCalendarAlt /> }
            ]
        },
        {
            heading: 'Engagement & Insights',
            items: [
                { path: '/recruiter/notifications', name: 'Notifications', icon: <FaBell /> },
                { path: '/recruiter/messages', name: 'Communication', icon: <FaComments /> },
                { path: '/recruiter/reports', name: 'Hiring Analytics', icon: <FaChartBar /> }
            ]
        },
        {
            heading: 'User',
            items: [
                { path: '/recruiter/profile', name: 'My Profile', icon: <FaUser /> }
            ]
        }
    ];

    return (
        <div className="recruiter-dashboard-wrapper">
            <div className={`recruiter-layout ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
                {/* Sidebar */}
                <aside className="sidebar">
                    <button
                        className="sidebar-boundary-toggle"
                        onClick={toggleSidebar}
                        aria-label={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
                    >
                        <FaChevronLeft />
                    </button>

                    <div className="sidebar-header">
                        <div className="logo-container">
                            {isSidebarOpen ? (
                                <h2 className="logo-text">ZenX<span className="accent">Staffing</span></h2>
                            ) : (
                                <h2 className="logo-text" style={{ margin: "0 50% 70% 0" }}>Z<span className="accent">S</span></h2>
                            )}
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        {menuGroups.map((group, groupIndex) => (
                            <div key={groupIndex} className="nav-group">
                                <h3 className="recruiter-nav-heading">{group.heading}</h3>
                                <ul>
                                    {group.items.map((item, index) => (
                                        <li key={index}>
                                            <NavLink
                                                to={item.path}
                                                end={item.path === '/recruiter'}
                                                className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                                                title={!isSidebarOpen ? item.name : ""}
                                            >
                                                <span className="nav-icon">{item.icon}</span>
                                                <span className="nav-text">{item.name}</span>
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </nav>

                    <div className="sidebar-footer">
                        <button className="logout-btn" onClick={handleLogout}>
                            <span className="nav-icon"><FaSignOutAlt /></span>
                            <span className="nav-text">Sign Out</span>
                        </button>
                    </div>
                </aside>

                {/* Main Content Area */}
                <div className="main-content-wrapper">
                    <header className="topbar">
                        <div className="topbar-left">
                            {/* Space for Breadcrumbs or Search if needed later */}
                        </div>

                        <div className="topbar-right">
                            <div
                                className={`notification-trigger ${unreadCount > 0 ? 'has-unread' : ''}`}
                                onClick={() => navigate('/recruiter/notifications')}
                                title="Notifications"
                            >
                                <FaBell size={18} />
                                {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
                            </div>

                            <div className="recruiter-profile-summary" onClick={() => navigate('/recruiter/profile')}>
                                <div className="avatar">
                                    {profileImage ? (
                                        <img src={`${API_URL}${profileImage}`} alt="Profile" />
                                    ) : (
                                        recruiterName.charAt(0).toUpperCase()
                                    )}
                                </div>
                                <div className="profile-text">
                                    <span className="name">{recruiterName}</span>
                                    <span className="role">Recruiter</span>
                                </div>
                            </div>
                        </div>
                    </header>

                    <main className="main-content">
                        <Outlet />
                    </main>
                </div>
            </div>
        </div>
    );
};

export default RecruiterLayout;
